import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface OrderResult {
  type: 'order';
  orderRef: string;
  name: string;
  phone: string;
  city: string;
  finalPrice: number;
  status: string;
  date: string;
}

interface CustomerResult {
  type: 'customer';
  id: string;
  name: string;
  phone: string;
  orderCount: number;
  totalSpent: number;
  joinedAt: string;
}

type SearchResult = OrderResult | CustomerResult;

const STATUS_MAP: Record<string, { label: string; cls: string }> = {
  pending:   { label: 'قيد المعالجة', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  confirmed: { label: 'تم التأكيد',   cls: 'bg-green-50 text-green-700 border-green-200' },
  shipped:   { label: 'تم الشحن',     cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  delivered: { label: 'تم التوصيل',   cls: 'bg-teal-50 text-teal-700 border-teal-200' },
  cancelled: { label: 'ملغي',          cls: 'bg-red-50 text-red-700 border-red-200' },
};

interface GlobalSearchProps {
  onNavigate: (tab: string) => void;
}

export default function GlobalSearch({ onNavigate }: GlobalSearchProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback(async (q: string) => {
    if (!q.trim() || q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    const [ordersRes, customersRes] = await Promise.all([
      supabase
        .from('orders')
        .select('order_ref, name, phone, city, final_price, status, created_at')
        .or(`order_ref.ilike.%${q}%,name.ilike.%${q}%,phone.ilike.%${q}%,city.ilike.%${q}%`)
        .order('created_at', { ascending: false })
        .limit(5),
      supabase
        .from('customers')
        .select('id, name, phone, joined_at')
        .or(`name.ilike.%${q}%,phone.ilike.%${q}%`)
        .order('joined_at', { ascending: false })
        .limit(5),
    ]);

    // Enrich customers with order stats
    const customerPhones = (customersRes.data ?? []).map((c: { phone: string }) => c.phone);
    let statsMap: Record<string, { count: number; total: number }> = {};
    if (customerPhones.length > 0) {
      const { data: ordData } = await supabase
        .from('orders')
        .select('phone, final_price')
        .in('phone', customerPhones);
      (ordData ?? []).forEach((o: { phone: string; final_price: number }) => {
        if (!statsMap[o.phone]) statsMap[o.phone] = { count: 0, total: 0 };
        statsMap[o.phone].count += 1;
        statsMap[o.phone].total += Number(o.final_price ?? 0);
      });
    }

    const orderResults: OrderResult[] = (ordersRes.data ?? []).map((o: {
      order_ref: string; name: string; phone: string; city: string;
      final_price: number; status: string; created_at: string;
    }) => ({
      type: 'order',
      orderRef: o.order_ref,
      name: o.name,
      phone: o.phone,
      city: o.city,
      finalPrice: Number(o.final_price),
      status: o.status,
      date: o.created_at,
    }));

    const customerResults: CustomerResult[] = (customersRes.data ?? []).map((c: {
      id: string; name: string; phone: string; joined_at: string;
    }) => ({
      type: 'customer',
      id: c.id,
      name: c.name,
      phone: c.phone,
      orderCount: statsMap[c.phone]?.count ?? 0,
      totalSpent: statsMap[c.phone]?.total ?? 0,
      joinedAt: c.joined_at,
    }));

    setResults([...orderResults, ...customerResults]);
    setLoading(false);
    setFocused(-1);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); return; }
    setLoading(true);
    debounceRef.current = setTimeout(() => search(query), 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, search]);

  // Keyboard shortcut: Ctrl+K or Cmd+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
        setResults([]);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Arrow key navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocused(f => Math.min(f + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocused(f => Math.max(f - 1, -1));
    } else if (e.key === 'Enter' && focused >= 0) {
      handleSelect(results[focused]);
    }
  };

  const handleSelect = (result: SearchResult) => {
    if (result.type === 'order') {
      onNavigate('orders');
    } else {
      onNavigate('customers');
    }
    setOpen(false);
    setQuery('');
    setResults([]);
  };

  const highlight = (text: string, q: string) => {
    if (!q.trim()) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="bg-amber-100 text-amber-800 rounded px-0.5">{text.slice(idx, idx + q.length)}</mark>
        {text.slice(idx + q.length)}
      </>
    );
  };

  const orderResults = results.filter(r => r.type === 'order') as OrderResult[];
  const customerResults = results.filter(r => r.type === 'customer') as CustomerResult[];
  const hasResults = results.length > 0;

  return (
    <div ref={containerRef} className="relative" dir="rtl">
      {/* Trigger Button */}
      <button
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 50);
        }}
        className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 rounded-lg px-3 py-2 text-sm transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-search-line text-sm"></i>
        <span className="hidden md:block text-xs">بحث سريع...</span>
        <span className="hidden lg:flex items-center gap-0.5 text-[10px] font-bold text-gray-400 bg-white border border-gray-200 px-1.5 py-0.5 rounded-md">
          <span>⌘</span><span>K</span>
        </span>
      </button>

      {/* Modal Overlay */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4" dir="rtl">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setOpen(false)}></div>

          {/* Search Panel */}
          <div className="relative w-full max-w-2xl bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            {/* Input Row */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
              <div className="w-9 h-9 flex items-center justify-center bg-[#1a1a1a] rounded-xl flex-shrink-0">
                {loading ? (
                  <i className="ri-loader-4-line text-white text-sm animate-spin"></i>
                ) : (
                  <i className="ri-search-line text-white text-sm"></i>
                )}
              </div>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="ابحث عن طلب برقمه أو اسم العميل أو جواله أو المدينة..."
                className="flex-1 text-sm text-[#1a1a1a] outline-none placeholder-gray-400 bg-transparent"
                autoComplete="off"
              />
              {query && (
                <button
                  onClick={() => { setQuery(''); setResults([]); inputRef.current?.focus(); }}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
              <button
                onClick={() => { setOpen(false); setQuery(''); setResults([]); }}
                className="text-[11px] font-bold text-gray-400 bg-gray-100 px-2 py-1 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors whitespace-nowrap"
              >
                Esc
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[420px] overflow-y-auto">
              {!query.trim() ? (
                <div className="px-5 py-8 text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-xl mx-auto mb-3">
                    <i className="ri-search-2-line text-2xl text-gray-300"></i>
                  </div>
                  <p className="text-sm font-medium text-gray-400">ابحث عن طلب أو عميل</p>
                  <p className="text-xs text-gray-300 mt-1">رقم الطلب · اسم العميل · رقم الجوال · المدينة</p>
                  <div className="flex items-center justify-center gap-4 mt-4">
                    <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
                      <div className="w-5 h-5 flex items-center justify-center bg-gray-100 rounded text-[9px] font-bold">↑</div>
                      <div className="w-5 h-5 flex items-center justify-center bg-gray-100 rounded text-[9px] font-bold">↓</div>
                      للتنقل
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
                      <div className="px-1.5 h-5 flex items-center justify-center bg-gray-100 rounded text-[9px] font-bold">Enter</div>
                      للفتح
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
                      <div className="px-1.5 h-5 flex items-center justify-center bg-gray-100 rounded text-[9px] font-bold">Esc</div>
                      للإغلاق
                    </div>
                  </div>
                </div>
              ) : loading && results.length === 0 ? (
                <div className="flex items-center justify-center py-10 gap-2 text-gray-400">
                  <i className="ri-loader-4-line animate-spin text-base"></i>
                  <span className="text-sm">جاري البحث...</span>
                </div>
              ) : !hasResults && query.length >= 2 ? (
                <div className="px-5 py-10 text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-xl mx-auto mb-3">
                    <i className="ri-search-eye-line text-2xl text-gray-300"></i>
                  </div>
                  <p className="text-sm font-medium text-gray-500">لا توجد نتائج لـ &quot;{query}&quot;</p>
                  <p className="text-xs text-gray-400 mt-1">جرّب البحث برقم الجوال أو رقم الطلب</p>
                </div>
              ) : (
                <div className="py-2">
                  {/* Orders Section */}
                  {orderResults.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 px-5 py-2">
                        <i className="ri-file-list-3-line text-xs text-gray-400"></i>
                        <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">الطلبات</span>
                        <span className="text-[10px] font-bold text-gray-300 bg-gray-100 px-1.5 py-0.5 rounded-full">{orderResults.length}</span>
                      </div>
                      {orderResults.map((r, idx) => {
                        const globalIdx = idx;
                        const st = STATUS_MAP[r.status] ?? { label: r.status, cls: 'bg-gray-50 text-gray-500 border-gray-200' };
                        return (
                          <button
                            key={r.orderRef}
                            onClick={() => handleSelect(r)}
                            onMouseEnter={() => setFocused(globalIdx)}
                            className={`w-full text-right flex items-center gap-4 px-5 py-3 transition-colors cursor-pointer ${
                              focused === globalIdx ? 'bg-gray-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="w-9 h-9 flex items-center justify-center bg-[#1a1a1a]/5 rounded-xl flex-shrink-0">
                              <i className="ri-file-list-3-line text-[#1a1a1a] text-sm"></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-bold text-[#1a1a1a]">#{highlight(r.orderRef, query)}</span>
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                              </div>
                              <p className="text-xs text-gray-400 mt-0.5 truncate">
                                {highlight(r.name, query)} · {highlight(r.phone, query)} · {highlight(r.city, query)}
                              </p>
                            </div>
                            <div className="text-left flex-shrink-0">
                              <p className="text-sm font-bold text-[#1a1a1a]">{r.finalPrice.toLocaleString('ar-SA')} ر.س</p>
                              <p className="text-[10px] text-gray-400">
                                {new Date(r.date).toLocaleDateString('ar-SA', { month: 'short', day: 'numeric' })}
                              </p>
                            </div>
                            <i className="ri-arrow-left-s-line text-gray-300 text-sm flex-shrink-0"></i>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Customers Section */}
                  {customerResults.length > 0 && (
                    <div className={orderResults.length > 0 ? 'mt-1 border-t border-gray-50 pt-1' : ''}>
                      <div className="flex items-center gap-2 px-5 py-2">
                        <i className="ri-group-line text-xs text-gray-400"></i>
                        <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">العملاء</span>
                        <span className="text-[10px] font-bold text-gray-300 bg-gray-100 px-1.5 py-0.5 rounded-full">{customerResults.length}</span>
                      </div>
                      {customerResults.map((r, idx) => {
                        const globalIdx = orderResults.length + idx;
                        return (
                          <button
                            key={r.id}
                            onClick={() => handleSelect(r)}
                            onMouseEnter={() => setFocused(globalIdx)}
                            className={`w-full text-right flex items-center gap-4 px-5 py-3 transition-colors cursor-pointer ${
                              focused === globalIdx ? 'bg-gray-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="w-9 h-9 flex items-center justify-center bg-[#1a1a1a] rounded-xl flex-shrink-0">
                              <span className="text-white text-sm font-bold">{r.name.charAt(0)}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-bold text-[#1a1a1a]">{highlight(r.name, query)}</span>
                                {r.orderCount > 0 && (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                                    {r.orderCount} طلب
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 mt-0.5">{highlight(r.phone, query)}</p>
                            </div>
                            <div className="text-left flex-shrink-0">
                              {r.totalSpent > 0 && (
                                <p className="text-sm font-bold text-[#1a1a1a]">{r.totalSpent.toLocaleString('ar-SA')} ر.س</p>
                              )}
                              <p className="text-[10px] text-gray-400">
                                {new Date(r.joinedAt).toLocaleDateString('ar-SA', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            </div>
                            <i className="ri-arrow-left-s-line text-gray-300 text-sm flex-shrink-0"></i>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Quick Actions Footer */}
                  {hasResults && (
                    <div className="border-t border-gray-100 px-5 py-3 flex items-center justify-between">
                      <p className="text-[11px] text-gray-400">
                        {results.length} نتيجة
                      </p>
                      <div className="flex items-center gap-3 text-[11px] text-gray-400">
                        {orderResults.length > 0 && (
                          <button
                            onClick={() => { onNavigate('orders'); setOpen(false); setQuery(''); setResults([]); }}
                            className="flex items-center gap-1 hover:text-[#1a1a1a] transition-colors cursor-pointer"
                          >
                            <i className="ri-file-list-3-line text-xs"></i>
                            عرض كل الطلبات
                          </button>
                        )}
                        {customerResults.length > 0 && (
                          <button
                            onClick={() => { onNavigate('customers'); setOpen(false); setQuery(''); setResults([]); }}
                            className="flex items-center gap-1 hover:text-[#1a1a1a] transition-colors cursor-pointer"
                          >
                            <i className="ri-group-line text-xs"></i>
                            عرض كل العملاء
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
